<template>
  <div>
    <div v-if="!isRedirectableSharelink" class="app-background" id="app-layout">
      <div v-if="getLangLoadStatus">
        <navigation-bar v-if="showNav"></navigation-bar>
        <id-login-background class="background-img" v-if="legacyAccount"></id-login-background>
        <router-view></router-view>
        <debug-panel></debug-panel>
        <copyright-footer v-if="showFooter"></copyright-footer>
      </div>
      <div class="app-container" uk-height-viewport="expand: true" v-else>
        <div class="uk-position-center">
          <spinner></spinner>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import UIkit from 'uikit'
  import Icons from 'uikit/dist/js/uikit-icons'
  import NavigationBar from './components/navigation-bar/navigation-bar'
  import CopyrightFooter from './components/footer/copyright-footer'
  import {idAuthenticator} from './plugins/auth'
  import SHA256 from 'js-sha256'
  import {mapGetters, mapMutations, mapActions} from 'vuex'
  import {setLocale} from './localization/index'
  import * as routes from './enums/routes'
  import * as event from './enums/mixpanel-event'
  import detectionUtil from './utils/device-detection-util'
  import environment from './plugins/environment'
  import * as url from './enums/static-urls'
  import mixpanel from './services/mixpanel'
  import DebugPanel from './components/debug-panel/debug-panel'
  import * as contentType from './enums/content-type'
  import * as subjects from './enums/subjects'
  import * as sorting from './enums/sorting'
  import * as countries from './enums/supported-countries'
  import IdLoginBackground from './components/ui-components/backgrounds/id-login-background'
  import spinner from './components/ui-components/spinner/spinner'
  import { SLSO } from './enums/clients'

  UIkit.use(Icons)

  export default {
    name: 'app',
    components: {
      DebugPanel,
      NavigationBar,
      CopyrightFooter,
      IdLoginBackground,
      spinner
    },
    data() {
      return {
        lang: null,
        legacyAccount: false,
        showNav: true,
        showFooter: true
      }
    },
    computed: {
      ...mapGetters(['getUserProfile', 'filters', 'sorting', 'getLangLoadStatus']),
      isRedirectableSharelink() {
        return this.$feature('share-link-redirect').enabled &&
          this.$route.name === 'Share' &&
          this.$route.query.src !== 'notebook'
      }
    },
    methods: {
      ...mapMutations(['setFilterStrings', 'updateSortingItems', 'updateUserLocation']),
      ...mapActions(['updateFilterStrings', 'resolveUserLocation', 'updateLangLoadStatus']),
      setIdentity() {
        if (idAuthenticator.isAuthenticated()) {
          idAuthenticator.getProfile((profile) => {
            let hashId = SHA256(profile.id)
            mixpanel.identify(hashId)
            mixpanel.setProperties({'SMART Account': hashId})
          })
        } else {
          mixpanel.reset()
          mixpanel.setProperties({'Client ID': mixpanel.getDistinctId()})
        }
      },
      queryHandler() {
        let query = Object.assign({}, this.$route.query)
        if (query.testbot) {
          if (!mixpanel.hasOptedOut()) {
            mixpanel.optOut()
          }
        } if (query.notebook) {
          mixpanel.track(event.NOTEBOOK_REDIRECT)
          delete query.notebook
          this.$router.replace({query})
        } if (query.source) {
          mixpanel.track(event.NOTEBOOK_REDIRECT)
          delete query.source
          this.$router.replace({query})
        } if (query.lang) {
          this.lang = query.lang
        }
      },
      routeHandler(value) {
        switch (value.name) {
          case routes.LEGACY_SIGN_UP:
            this.legacyAccount = true
            this.showNav = false
            this.showFooter = false
            break
          case routes.LEGACY_SIGN_IN:
            this.legacyAccount = true
            this.showNav = false
            this.showFooter = false
            break
          default:
            break
        }
      },
      redirect (hostName, path, search) {
        if (environment.getEnv() === 'prod') {
          if (!hostName.match('smarttech-prod')) {
            window.open(url.EXCHANGE_SMARTTECH_PROD + path + search, '_self')
          }
        }
      },
      contentUrlRedirect(hostName, path, search) {
        if (environment.getEnv() === 'prod') {
          if (hostName.match('content')) {
            window.open(url.SMART_EXCHANGE + path + search, '_self')
          }
        }
      },
      resolveLang() {
        setLocale(this.lang)
            .then(() => {
              this.resolveResourceTypes()
              this.resolveSubjects()
              this.resolveSorting()
              this.resolveGrades()
              this.resolveCountries()
              this.updateLangLoadStatus(true)
            })
      },
      resolveGrades() {
        let gradesFilters = this.filters.find(o => o.title === 'Grades').items
        let gradesArr = []
        for (let item of gradesFilters) {
          let gradeObj = {}
          gradeObj.id = item.id
          gradeObj.selected = item.selected
          if (this.$i18n.locale === 'en' || this.$i18n.locale === 'en-US') {
            gradeObj.title = item.title
          } else {
            if (item.id === 'all') {
              gradeObj.title = this.$t('general.allAges')
            } else {
              gradeObj.title = item.altTitle
            }
          }
          gradesArr.push(gradeObj)
        }
        this.updateFilterStrings({filterGroup: 'Grades', items: gradesArr})
      },
      resolveResourceTypes() {
        let searchFilters = this.filters.find(o => o.title === 'Resource Type').items
        let resourceTypeArr = []
        for (let item of searchFilters) {
          let resourceTypeObj = {}
          resourceTypeObj.id = item.id
          resourceTypeObj.selected = item.selected
          switch (item.id) {
            case 'all':
              resourceTypeObj.title = this.$t('resourceTypes.allTypes')
              break
            case contentType.LESSON:
              resourceTypeObj.title = this.$t('resourceTypes.lessons')
              break
            case contentType.ACTIVITY:
              resourceTypeObj.title = this.$t('resourceTypes.activities')
              break
            case contentType.ASSESSMENT:
              resourceTypeObj.title = this.$t('resourceTypes.assessments')
              break
            case contentType.TEMPLATE:
              resourceTypeObj.title = this.$t('resourceTypes.templates')
              break
            case contentType.CLASSROOM_TOOLS:
              resourceTypeObj.title = this.$t('resourceTypes.teacherTools')
              break
          }
          resourceTypeArr.push(resourceTypeObj)
        }
        this.updateFilterStrings({filterGroup: 'Resource Type', items: resourceTypeArr})
      },
      resolveSubjects() {
        let searchFilters = this.filters.find(o => o.title === 'Subjects').items
        let subjectArr = []
        for (let item of searchFilters) {
          let subjectObj = {}
          subjectObj.id = item.id
          subjectObj.selected = item.selected
          switch (item.id) {
            case subjects.ALL_SUBJECTS_ID:
              subjectObj.title = this.$t('subjects.allSubjects')
              break
            case subjects.MATHEMATICS_ID:
              subjectObj.title = this.$t('subjects.mathematics')
              break
            case subjects.SCIENCE_ID:
              subjectObj.title = this.$t('subjects.science')
              break
            case subjects.SOCIAL_STUDIES_ID:
              subjectObj.title = this.$t('subjects.socialStudies')
              break
            case subjects.ARTS_EDUCATION_ID:
              subjectObj.title = this.$t('subjects.artsEducation')
              break
            case subjects.CAREER_TECHNICAL_EDUCATION_ID:
              subjectObj.title = this.$t('subjects.careerTechnicalEducation')
              break
            case subjects.HEALTH_AND_PE_ID:
              subjectObj.title = this.$t('subjects.healthAndPE')
              break
            case subjects.LANGUAGE_ARTS_ID:
              subjectObj.title = this.$t('subjects.languageArts')
              break
            case subjects.TECHNOLOGY_EDUCATION_ID:
              subjectObj.title = this.$t('subjects.technologyEducation')
              break
            case subjects.WORLD_LANGUAGES_ID:
              subjectObj.title = this.$t('subjects.worldLanguages')
              break
            case subjects.LIBRARY_MEDIA_ID:
              subjectObj.title = this.$t('subjects.libraryMedia')
              break
          }
          subjectArr.push(subjectObj)
        }
        let sortedSubjects = subjectArr.sort((a, b) => a.title.localeCompare(b.title)).reduce((acc, e) => {
          e.id === 'all' ? acc.unshift(e) : acc.push(e)
          return acc
        }, [])
        this.updateFilterStrings({filterGroup: 'Subjects', items: sortedSubjects})
      },
      resolveSorting() {
        let sortingFilters = this.sorting.items
        let sortingArr = []
        for (let item of sortingFilters) {
          let sortingObj = {}
          sortingObj.id = item.id
          sortingObj.selected = item.selected
          switch (item.id) {
            case sorting.BEST_MATCH_ID:
              sortingObj.title = this.$t('general.bestMatch')
              break
            case sorting.SORT_BY_MOST_POPULAR:
              sortingObj.title = this.$t('general.mostPopular')
              break
            case sorting.SORT_BY_FEATURED:
              sortingObj.title = this.$t('general.featured')
          }
          sortingArr.push(sortingObj)
        }
        this.updateSortingItems(sortingArr)
      },
      resolveCountries () {
        let supportedCountries = this.$t('countries')
        let supportedCountriesArr = []
        let allCountriesObj = {id: 'all', title: this.$t('general.allLocations'), selected: true}
        supportedCountriesArr.push(allCountriesObj)
        for (let country in supportedCountries) {
          let countryObj = {}
          countryObj.id = countries.SUPPORTED_COUNTRIES.find(o => o.title === country).ID
          countryObj.title = supportedCountries[country]
          countryObj.selected = false
          supportedCountriesArr.push(countryObj)
        }
        this.updateFilterStrings({filterGroup: 'Location', items: supportedCountriesArr})
      },
      resolveLocation() {
        try {
          this.resolveUserLocation()
              .then(location => {
                this.updateUserLocation(location.body)
              })
        } catch (err) {}
      },

      resolveSLSShareUrl(contentId) {
        let url = environment.serviceUrlFor(SLSO) + '/share/' + contentId
        let params = []
        if (environment.getEnv() !== 'prod' && environment.getEnv() !== 'preprod') {
          params.push('env=' + environment.getEnv())
        }
        if (params.length > 0) {
          let queryString = ''
          for (let value of params) {
            queryString += value + '&'
          }
          url += '?' + queryString.substring(0, queryString.length - 1)
        }
        return url
      },
      validateSLSRedirect() {
        if (this.isRedirectableSharelink) {
          window.open(this.resolveSLSShareUrl(this.$route.params.id), '_self')
        }
      }
    },
    created() {
      this.validateSLSRedirect()
      const browserRejectsCrossSiteCookies = detectionUtil.isSafari() ||
        (detectionUtil.isChrome() && detectionUtil.chromeVersion() >= 80) ||
        detectionUtil.isFirefox()
      if (browserRejectsCrossSiteCookies && !detectionUtil.isGoogleBot()) {
        this.redirect(window.location.hostname, window.location.pathname, window.location.search)
      } else {
        this.contentUrlRedirect(window.location.hostname, window.location.pathname, window.location.search)
      }
      this.queryHandler()
      this.resolveLang()
      this.routeHandler(this.$route)
    },
    mounted() {
      this.setIdentity()
      this.resolveLocation()
    },
    watch: {
      getUserProfile() {
        this.setIdentity()
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import 'styles/colours';
  @import 'styles/breakpoints';

  @media screen and (max-width: $breakpoint-medium) {
    .app-container {
      width: 100%;
    }
  }

  .background-img {
    width: 100%;
    height: 100%;
  }

  .app-background {
    background-color: $app-background;
  }
</style>
